/**
 * Global Subscription Handler Component
 *
 * Handles global WebSocket subscriptions and emits events
 * Components can listen to 'globalNotification' events to handle notifications
 * This component must be inside AuthProvider
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import {
  subscribeToUserEvents,
  unsubscribeFromUserEvents,
} from "services/globalSubscriptionService";
import { getUserProfileId } from "config/cognito";
import {
  applyWebRefreshFromSubscription,
  parseGlobalRefreshEvent,
  shouldUseDataAsNotificationBody,
} from "utils/globalSubscriptionEvents";

function buildNotificationDetail(message) {
  const parsed = parseGlobalRefreshEvent(message);
  const skipDataBody = parsed && !shouldUseDataAsNotificationBody(parsed);

  const messageType = message.MessageType || message.Type || "info";
  const normalizedType =
    typeof messageType === "string" ? messageType.toLowerCase() : "info";

  let messageText =
    message.Message || message.message || message.Data || message.data;
  if (skipDataBody) {
    messageText =
      message.Message ||
      message.message ||
      (parsed?.messageType === "missionid"
        ? "Opening mission details…"
        : parsed?.messageType === "mission"
          ? "Mission updated"
          : parsed?.messageType === "task"
            ? "Tasks updated"
            : "Update received");
  } else if (messageText == null || messageText === "") {
    messageText = "New notification received";
  } else if (
    typeof messageText === "string" &&
    messageText.trim().startsWith("{")
  ) {
    try {
      const parsedJson = JSON.parse(messageText);
      messageText =
        parsedJson.Message ||
        parsedJson.message ||
        parsedJson.msg ||
        parsedJson.text ||
        messageText;
    } catch {
      // Use as-is if not valid JSON
    }
  } else if (typeof messageText === "object") {
    messageText =
      messageText.Message ||
      messageText.message ||
      messageText.text ||
      JSON.stringify(messageText);
  }

  const snackbarType = /success/i.test(String(message.Message ?? ""))
    ? "success"
    : parsed?.handleOnWeb
      ? "info"
      : normalizedType;

  return {
    ...message,
    type: snackbarType,
    title:
      snackbarType === "success"
        ? "Success"
        : snackbarType === "error"
          ? "Error"
          : snackbarType === "warning"
            ? "Warning"
            : "Notification",
    message: String(messageText),
  };
}

export default function GlobalSubscriptionHandler() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const profileId = getUserProfileId(user);

      if (profileId) {
        subscribeToUserEvents(
          profileId,
          (message) => {
            console.log("📨 Global event received:", message);

            applyWebRefreshFromSubscription(message, navigate);

            // Refresh events only trigger data reload/navigation — no snackbar.
            if (parseGlobalRefreshEvent(message)) {
              return;
            }

            window.dispatchEvent(
              new CustomEvent("globalNotification", {
                detail: buildNotificationDetail(message),
              }),
            );
          },
          (err) => {
            console.error("❌ Global subscription error:", err);
          },
        );

        return () => {
          unsubscribeFromUserEvents();
        };
      }
    } else {
      unsubscribeFromUserEvents();
    }
  }, [user, navigate]);

  return null;
}
